import React, { useEffect, useState } from 'react';
import { Form, Input, Row, Switch, Typography } from 'antd';
import { gql, useMutation, useQuery } from '@common/hooks';
import debounce from 'lodash/debounce';
import {
  AppContent,
  Banner,
  ErrorHandler,
  HistoryDropDown,
  LoadingState,
} from '@common/components';
import { HistoryInfoFragment, HistoryInfoFragmentType } from '@common/graphql';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ConfirmationModal } from '@scheduling/components';
const { Text } = Typography;

const OrganizationUnitQuery = gql`
  query OrganizationUnitBusinessPath(
    $organizationUnitId: ID!
    $scope: String!
  ) {
    organizationUnit(uuid: $organizationUnitId, scope: $scope) {
      id
      businessPath
      unitIntegrationConfigurationsHistory {
        ...HistoryInfoFragment
      }
      shiftIngestionType
    }
  }
  ${HistoryInfoFragment}
`;

const UpdateUnitIntegrationConfigurations = gql`
  mutation UpdateUnitIntegrationConfigurations(
    $organizationUnitId: ID!
    $input: UpdateUnitIntegrationConfigurationsInput!
  ) {
    updateUnitIntegrationConfigurations(
      organizationUnitId: $organizationUnitId
      input: $input
    ) {
      organizationUnit {
        id
        businessPath
        unitIntegrationConfigurationsHistory {
          ...HistoryInfoFragment
        }
        shiftIngestionType
      }
      errors
    }
  }
  ${HistoryInfoFragment}
`;

type OrganizationUnitQueryArgs = {
  organizationUnitId: string;
  scope: string;
};

type UpdateUnitIntegrationConfigurationsArgs = {
  organizationUnitId: string;
  input: {
    businessPath: string;
    integrationActive: boolean;
  };
};

type UpdateUnitIntegrationConfigurationsResponse = {
  updateUnitIntegrationConfigurations: {
    organizationUnit: OrganizationUnit;
    errors: string[];
  };
};

type OrganizationUnit = {
  id: string;
  businessPath: string;
  unitIntegrationConfigurationsHistory: HistoryInfoFragmentType[];
  shiftIngestionType: string;
};

type OrganizationUnitResponse = {
  organizationUnit: OrganizationUnit;
};

type Props = {
  organizationUnitId: string;
  disabled: boolean;
};

type Status = 'neutral' | 'success' | 'error';

export const IntegrationForm = ({ organizationUnitId, disabled }: Props) => {
  const [form] = Form.useForm();
  const [status, setStatus] = useState<Status>('neutral');
  const [statusMessage, setStatusMessage] = useState('');
  const [integrationActive, setIntegrationActive] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const query = useQuery<OrganizationUnitResponse, OrganizationUnitQueryArgs>(
    OrganizationUnitQuery,
    {
      variables: {
        organizationUnitId,
        scope: 'organization',
      },
    }
  );
  const [updateBusinessPath, { data: formData, loading, error }] = useMutation<
    UpdateUnitIntegrationConfigurationsResponse,
    UpdateUnitIntegrationConfigurationsArgs
  >(UpdateUnitIntegrationConfigurations);

  useEffect(() => {
    if (loading) {
      setStatus('neutral');
      setStatusMessage('Loading...');
    } else if (error) {
      createMessage('error', 'Error occurred. Please try again.');
      form.resetFields();
    } else if (formData) {
      if (formData.updateUnitIntegrationConfigurations.errors.length > 0) {
        setStatus('error');
        setStatusMessage(
          formData.updateUnitIntegrationConfigurations.errors[0]
        );
      } else {
        setStatus('success');
        setStatusMessage('Integration configurations saved.');
      }
    }
  }, [form, formData, loading, error]);

  useEffect(() => {
    if (formData?.updateUnitIntegrationConfigurations) {
      setIntegrationActive(
        formData.updateUnitIntegrationConfigurations.organizationUnit
          .shiftIngestionType !== 'off'
      );
    } else if (query.data) {
      setIntegrationActive(
        query.data.organizationUnit.shiftIngestionType !== 'off'
      );
    }
  }, [query.data, formData]);

  const handleSave = debounce(() => form.submit(), 1000);

  const handleCheckboxChange = (value: boolean) => {
    form.setFieldValue('integrationActive', value);
    if (!value && query.data?.organizationUnit.shiftIngestionType !== 'off') {
      setShowConfirmationModal(true);
    } else {
      handleSave();
    }
  };

  if (query.loading) return <LoadingState />;

  if (query.error) {
    return <ErrorHandler error={query.error} log="OrganizationUnitQuery" />;
  }

  const history =
    query.data?.organizationUnit?.unitIntegrationConfigurationsHistory ||
    formData?.updateUnitIntegrationConfigurations.organizationUnit
      ?.unitIntegrationConfigurationsHistory ||
    [];

  return (
    <AppContent left={false} right={false}>
      {statusMessage && status && (
        <Row style={{ marginBottom: 20 }}>
          <Banner content={statusMessage} type={status} />
        </Row>
      )}
      <ConfirmationModal
        visible={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={() => {
          handleSave();
          setShowConfirmationModal(false);
        }}
      />
      <Form
        disabled={disabled}
        form={form}
        initialValues={{
          integrationType: 'UKG',
          businessPath: query.data?.organizationUnit.businessPath,
          integrationActive:
            query.data?.organizationUnit.shiftIngestionType !== 'off',
        }}
        layout="vertical"
        onFinish={(values) => {
          updateBusinessPath({
            variables: {
              organizationUnitId,
              input: {
                businessPath: values.businessPath,
                integrationActive: values.integrationActive,
              },
            },
          });
        }}
      >
        <Row>
          <Form.Item name="integrationType" label="Integration Type">
            <Input disabled />
          </Form.Item>
        </Row>
        <Row>
          <Form.Item name="integrationActive" valuePropName="checked">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Switch
                checked={integrationActive}
                loading={loading}
                onChange={handleCheckboxChange}
              />
              <Text>Shift Ingestion Active</Text>
            </div>
          </Form.Item>
        </Row>
        <Row>
          <Form.Item name="businessPath" label="Business Path">
            <Input
              placeholder="Type the Business Path"
              onChange={handleSave}
              style={{ width: 450 }}
            />
          </Form.Item>
        </Row>
      </Form>
      {!query.loading && <HistoryDropDown data={history} hrVisible={false} />}
    </AppContent>
  );
};
